import { motion } from 'framer-motion';
import { ShieldCheck, BadgeCheck, EyeOff, Server, Lock, Clock } from 'lucide-react';

const badges = [
  {
    icon: <ShieldCheck className="h-6 w-6" />,
    title: 'PHIPA-Aligned',
    description: "Built to Ontario's Personal Health Information Protection Act standards. Patient data never leaves Canada.",
  },
  {
    icon: <BadgeCheck className="h-6 w-6" />,
    title: 'Role-Based Access',
    description: 'Clinic admins control who sees what. Staff, providers, and billing roles each have scoped permissions with a full audit log.',
  },
  {
    icon: <EyeOff className="h-6 w-6" />,
    title: 'Zero Model Training',
    description: 'Your clinical data is never used to train AI models. Each clinic is fully isolated.',
  },
  {
    icon: <Server className="h-6 w-6" />,
    title: 'Canadian Servers',
    description: 'All data processed and stored in GCP Montreal and Toronto regions — not the US.',
  },
  {
    icon: <Lock className="h-6 w-6" />,
    title: 'AES-256 Encryption',
    description: 'All recordings and notes encrypted at rest and in transit with industry-standard encryption.',
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Zero Retention Default',
    description: 'Aura-Chart audio and transcripts are automatically deleted within 24 hours. Nothing persists beyond that window by default.',
  },
];

export default function ComplianceBadges() {
  return (
    <section className="py-20 md:py-28 bg-navy-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm font-semibold text-primary-400 uppercase tracking-widest">
            Privacy & Security
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Built for Canadian healthcare from day one
          </h2>
          <p className="mt-4 text-lg text-white/50 max-w-xl mx-auto">
            Compliance isn't a checkbox. Every architectural decision was made with PHIPA in mind.
          </p>
        </motion.div>

        {/* Badge grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map((badge, i) => (
            <motion.div
              key={badge.title}
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
            >
              <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center text-primary-300 mb-3">
                {badge.icon}
              </div>
              <h3 className="text-base font-semibold text-white">{badge.title}</h3>
              <p className="mt-1.5 text-sm text-white/50 leading-relaxed">{badge.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Human-in-the-loop callout */}
        <motion.div
          className="mt-10 mx-auto max-w-2xl bg-white/5 border border-primary-400/25 rounded-2xl px-8 py-6 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className="text-xs font-semibold text-primary-400 uppercase tracking-widest mb-2">Clinician in Control</p>
          <p className="text-white/80 text-sm leading-relaxed">
            Aura-Chart converts the clinician's own voice into a draft note. The clinician reviews and approves before anything enters the patient record.{' '}
            <span className="text-white font-medium">AI assists documentation — clinicians make every clinical decision.</span>
          </p>
        </motion.div>

        {/* Security stats */}
        <motion.div
          className="mt-6 mx-auto max-w-2xl grid grid-cols-2 gap-4 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-white/5 border border-white/10 rounded-xl px-6 py-5">
            <p className="text-3xl font-extrabold text-white tabular-nums">0</p>
            <p className="text-sm text-white/50 mt-1 leading-snug">Known breaches since launch</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-6 py-5">
            <p className="text-3xl font-extrabold text-white tabular-nums">$11.5M</p>
            <p className="text-sm text-white/50 mt-1 leading-snug">Average healthcare data breach cost — IBM 2026</p>
          </div>
        </motion.div>

        {/* Security page link */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.55 }}
        >
          <a
            href="/security.html"
            className="text-sm text-white/40 hover:text-white/70 underline underline-offset-4 transition-colors"
          >
            Read our full security documentation →
          </a>
        </motion.div>
      </div>
    </section>
  );
}
